/**
 * CalendarSelectorDialog Component
 * Modal dialog for managing which calendars to read/write for a connection.
 * Users can toggle read access (Eye icon), select a primary write calendar (Star icon),
 * and override colors per sub-calendar.
 */

import { useState, useCallback } from 'react';
import { Eye, EyeOff, Star, RefreshCw, Loader2 } from 'lucide-react';
import type { CalendarSyncPreferenceDto, CalendarConnectionDto } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useCalendarsForConnection,
  useUpdateCalendarPreference,
  useRefreshCalendarList,
} from '@/hooks/queries/useCalendar';
import { CalendarColorPicker } from './CalendarColorPicker';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface CalendarSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  connection: CalendarConnectionDto | null;
  /** All colors in use across connections and sub-calendars */
  usedColors: Set<string>;
}

// Optimistic state for a calendar preference
interface OptimisticState {
  calendarId: string;
  readEventsEnabled?: boolean;
  isPrimaryWriteCalendar?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CalendarSelectorDialog({
  open,
  onClose,
  connection,
  usedColors,
}: CalendarSelectorDialogProps) {
  const connectionId = connection?.id;

  const {
    data: calendars,
    isLoading: calendarsLoading,
    error: calendarsError,
  } = useCalendarsForConnection(connectionId);

  const { mutate: updatePreference, isPending: updatePreferencePending } = useUpdateCalendarPreference();
  const { mutate: refreshCalendarList, isPending: refreshCalendarListPending } = useRefreshCalendarList();

  // Track optimistic updates
  const [optimisticUpdates, setOptimisticUpdates] = useState<Map<string, OptimisticState>>(
    new Map()
  );

  // Get the effective state for a calendar (optimistic or actual)
  const getEffectiveState = useCallback(
    (calendar: CalendarSyncPreferenceDto) => {
      const optimistic = optimisticUpdates.get(calendar.id);
      return {
        readEventsEnabled: optimistic?.readEventsEnabled ?? calendar.readEventsEnabled,
        isPrimaryWriteCalendar: optimistic?.isPrimaryWriteCalendar ?? calendar.isPrimaryWriteCalendar,
      };
    },
    [optimisticUpdates]
  );

  // Handle toggle for read events
  const handleToggleRead = useCallback(
    (calendar: CalendarSyncPreferenceDto) => {
      if (!connectionId) return;

      const currentState = getEffectiveState(calendar);
      const newValue = !currentState.readEventsEnabled;

      // Optimistic update
      setOptimisticUpdates((prev) => {
        const next = new Map(prev);
        next.set(calendar.id, {
          ...prev.get(calendar.id),
          calendarId: calendar.id,
          readEventsEnabled: newValue,
        });
        return next;
      });

      updatePreference(
        {
          connectionId,
          calendarId: calendar.providerCalendarId,
          request: { readEventsEnabled: newValue },
        },
        {
          onSuccess: () => {
            setOptimisticUpdates((prev) => {
              const next = new Map(prev);
              const existing = next.get(calendar.id);
              if (existing) {
                delete existing.readEventsEnabled;
                if (Object.keys(existing).length === 1) {
                  next.delete(calendar.id);
                }
              }
              return next;
            });
          },
          onError: () => {
            setOptimisticUpdates((prev) => {
              const next = new Map(prev);
              const existing = next.get(calendar.id);
              if (existing) {
                delete existing.readEventsEnabled;
                if (Object.keys(existing).length === 1) {
                  next.delete(calendar.id);
                }
              }
              return next;
            });
          },
        }
      );
    },
    [connectionId, getEffectiveState, updatePreference]
  );

  // Handle toggle for primary write calendar
  const handleTogglePrimary = useCallback(
    (calendar: CalendarSyncPreferenceDto) => {
      if (!connectionId) return;

      const currentState = getEffectiveState(calendar);
      const newValue = !currentState.isPrimaryWriteCalendar;

      setOptimisticUpdates((prev) => {
        const next = new Map(prev);

        if (newValue) {
          calendars?.forEach((cal) => {
            if (cal.id !== calendar.id) {
              const existing = next.get(cal.id);
              if (existing) {
                next.set(cal.id, { ...existing, isPrimaryWriteCalendar: false });
              } else if (cal.isPrimaryWriteCalendar) {
                next.set(cal.id, { calendarId: cal.id, isPrimaryWriteCalendar: false });
              }
            }
          });
        }

        next.set(calendar.id, {
          ...prev.get(calendar.id),
          calendarId: calendar.id,
          isPrimaryWriteCalendar: newValue,
        });

        return next;
      });

      updatePreference(
        {
          connectionId,
          calendarId: calendar.providerCalendarId,
          request: { isPrimaryWriteCalendar: newValue },
        },
        {
          onSuccess: () => {
            setOptimisticUpdates(new Map());
          },
          onError: () => {
            setOptimisticUpdates(new Map());
          },
        }
      );
    },
    [connectionId, calendars, getEffectiveState, updatePreference]
  );

  // Handle color change for a sub-calendar
  const handleColorChange = useCallback(
    (calendar: CalendarSyncPreferenceDto, color: string) => {
      if (!connectionId) return;
      updatePreference({
        connectionId,
        calendarId: calendar.providerCalendarId,
        request: { color },
      });
    },
    [connectionId, updatePreference]
  );

  // Handle color reset (inherit from connection)
  const handleColorReset = useCallback(
    (calendar: CalendarSyncPreferenceDto) => {
      if (!connectionId) return;
      updatePreference({
        connectionId,
        calendarId: calendar.providerCalendarId,
        request: { color: null },
      });
    },
    [connectionId, updatePreference]
  );

  // Handle refresh calendar list
  const handleRefresh = useCallback(() => {
    if (!connectionId) return;
    refreshCalendarList(connectionId);
  }, [connectionId, refreshCalendarList]);

  const isRefreshing = refreshCalendarListPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            Manage Calendars
          </DialogTitle>
          {connection && (
            <p className="text-sm text-muted-foreground">
              {connection.displayName || connection.accountEmail}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            The starred calendar receives shifts and tasks you create in the app.
          </p>
        </DialogHeader>

        <div className="py-4">
          {/* Loading State */}
          {calendarsLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading calendars...</span>
            </div>
          )}

          {/* Error State */}
          {calendarsError && (
            <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
              Failed to load calendars. Please try again.
            </div>
          )}

          {/* Calendar List */}
          {!calendarsLoading && !calendarsError && calendars && (
            <div className="space-y-2">
              {calendars.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No calendars found. Try refreshing the list.
                </div>
              ) : (
                <>
                  {/* Legend */}
                  <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Calendar</span>
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" /> Read
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="h-3 w-3" /> Write
                      </span>
                    </div>
                  </div>

                  {/* Calendar Rows */}
                  {calendars.map((calendar) => {
                    const effectiveState = getEffectiveState(calendar);
                    return (
                      <CalendarRow
                        key={calendar.id}
                        calendar={calendar}
                        connectionColor={connection?.color ?? null}
                        readEnabled={effectiveState.readEventsEnabled}
                        isPrimary={effectiveState.isPrimaryWriteCalendar}
                        onToggleRead={() => handleToggleRead(calendar)}
                        onTogglePrimary={() => handleTogglePrimary(calendar)}
                        onColorChange={(color) => handleColorChange(calendar, color)}
                        onColorReset={() => handleColorReset(calendar)}
                        usedColors={usedColors}
                        disabled={updatePreferencePending}
                      />
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || !connectionId}
          >
            {isRefreshing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh List
              </>
            )}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CalendarRow Component
// ============================================================================

interface CalendarRowProps {
  calendar: CalendarSyncPreferenceDto;
  connectionColor: string | null;
  readEnabled: boolean;
  isPrimary: boolean;
  onToggleRead: () => void;
  onTogglePrimary: () => void;
  onColorChange: (color: string) => void;
  onColorReset: () => void;
  usedColors: Set<string>;
  disabled: boolean;
}

function CalendarRow({
  calendar,
  connectionColor,
  readEnabled,
  isPrimary,
  onToggleRead,
  onTogglePrimary,
  onColorChange,
  onColorReset,
  usedColors,
  disabled,
}: CalendarRowProps) {
  // Show sub-calendar color: user override > provider color > connection color > grey
  const displayColor = calendar.color || calendar.providerColor || connectionColor || '#9ca3af';
  const hasOverride = !!calendar.color;

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-md px-3 py-2',
        'border border-border bg-card transition-colors',
        'hover:bg-accent/50'
      )}
    >
      {/* Left: Color dot and name */}
      <div className="flex items-center gap-3 min-w-0">
        <CalendarColorPicker
          selectedColor={calendar.color || calendar.providerColor}
          usedColors={usedColors}
          onColorSelect={onColorChange}
          onReset={hasOverride ? onColorReset : undefined}
          disabled={disabled}
        />
        <div className="min-w-0">
          <span className="truncate text-sm font-medium text-foreground block">
            {calendar.calendarName}
          </span>
          {!hasOverride && calendar.providerColor && (
            <span className="text-[0.65rem] text-muted-foreground">
              Using calendar color
            </span>
          )}
          {!hasOverride && !calendar.providerColor && (
            <span className="text-[0.65rem] text-muted-foreground">
              Using account color
            </span>
          )}
        </div>
      </div>

      {/* Right: Toggle buttons */}
      <div className="flex items-center gap-2">
        {/* Read Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleRead}
          disabled={disabled}
          className={cn(
            'h-8 w-8',
            readEnabled
              ? 'text-primary hover:text-primary'
              : 'text-muted-foreground hover:text-foreground'
          )}
          title={readEnabled ? 'Disable reading events' : 'Enable reading events'}
        >
          {readEnabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </Button>

        {/* Primary Write Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onTogglePrimary}
          disabled={disabled}
          className={cn(
            'h-8 w-8',
            isPrimary
              ? 'text-warning hover:text-warning'
              : 'text-muted-foreground hover:text-foreground'
          )}
          title={
            isPrimary
              ? 'Stop writing shifts/tasks to this calendar'
              : 'Write shifts and tasks to this calendar'
          }
        >
          <Star
            className={cn('h-4 w-4', isPrimary && 'fill-current')}
          />
        </Button>
      </div>
    </div>
  );
}

export default CalendarSelectorDialog;
